import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/imgs/Jhonks-main-logo.png";
import { useAuth } from "../../contexts/AuthContext";
import { useMode } from "../../contexts/ModeContext";
import { anonymousBinService } from "../../utils/anonymousBinService";
import { useBin, useNotificationCount } from "../../query/hooks";
import { binQuantity } from "../../query/bin";
import {
  HomeIcon,
  CubeIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  UserIcon,
  ChevronDownIcon,
  PhotoIcon,
  GiftIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  LifebuoyIcon,
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
} from "@heroicons/react/24/outline";
import WhatsAppButton from "./WhatsAppButton";

const Navbar = () => {
  const { user, logout, isAgent, isAdmin } = useAuth();
  const { isAgentMode, toggleMode } = useMode();
  const navigate = useNavigate();
  const location = useLocation();
  const isPublic = !user;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const binQuery = useBin(!!user);
  const noteQuery = useNotificationCount(!!user);
  const [anonCount, setAnonCount] = useState(0);
  const profileDropdownRef = useRef(null);
  const binCount = user ? binQuantity(binQuery.data) : anonCount;
  const notificationCount = user ? noteQuery.data || 0 : 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Handle mode toggle with mobile menu close
  const handleModeToggle = () => {
    toggleMode();
    setIsMobileMenuOpen(false);
    window.location.assign("/");
  };

  useEffect(() => {
    if (user) return undefined;
    const readAnon = () => {
      try {
        const anon = anonymousBinService.getBin();
        setAnonCount(Number(anon?.totalQuantity || 0));
      } catch (error) {
        setAnonCount(0);
      }
    };
    readAnon();
    window.addEventListener("jhonks-anonymous-bin-changed", readAnon);
    return () => window.removeEventListener("jhonks-anonymous-bin-changed", readAnon);
  }, [user]);

  const sellerNavItems = [
    { name: "Dashboard", href: "/", icon: HomeIcon },
    { name: "Materials", href: "/materials", icon: CubeIcon },
    { name: "My Bin", href: "/bin", icon: ShoppingCartIcon },
    { name: "Agents", href: "/agents", icon: UserGroupIcon },
    { name: "Bank Details", href: "/banks", icon: CreditCardIcon },
    { name: "Sell", href: "/sell", icon: CurrencyDollarIcon },
    { name: "Sale History", href: "/sales", icon: ClockIcon },
    { name: "Rewards", href: "/rewards", icon: GiftIcon },
    { name: "Referrals", href: "/referrals", icon: UserGroupIcon },
    { name: "Support", href: "/support", icon: LifebuoyIcon },
    { name: "Notifications", href: "/notifications", icon: ClockIcon },
  ];

  const agentNavItems = [
    { name: "Dashboard", href: "/", icon: HomeIcon },
    {
      name: "Pending Validations",
      href: "/agent/pending-validations",
      icon: ClockIcon,
    },
    {
      name: "Validation History",
      href: "/agent/validation-history",
      icon: ClockIcon,
    },
    {
      name: "Delivery History",
      href: "/agent/delivery-history",
      icon: ClockIcon,
    },
    { name: "Bank Details", href: "/banks", icon: CreditCardIcon },
    { name: "Support", href: "/support", icon: LifebuoyIcon },
    { name: "Notifications", href: "/notifications", icon: ClockIcon },
  ];

  const adminNavItems = [
    { name: "Dashboard", href: "/", icon: HomeIcon },
    { name: "Users", href: "/admin/users", icon: UserGroupIcon },
    { name: "Agents", href: "/admin/agents-manage", icon: UserGroupIcon },
    { name: "Manage Materials", href: "/admin/materials", icon: CubeIcon },
    { name: "Pending Validations", href: "/admin/pending-validations", icon: ClockIcon },
    { name: "Validation History", href: "/admin/validation-history", icon: ClockIcon },
    { name: "All Sales", href: "/admin/sales", icon: CurrencyDollarIcon },
    { name: "All Deliveries", href: "/admin/deliveries", icon: ClockIcon },
    { name: "Unpaid payments", href: "/admin/payments", icon: CurrencyDollarIcon },
    { name: "Bounty Management", href: "/admin/bounties", icon: GiftIcon },
    { name: "Rewards Claims", href: "/admin/rewards-management", icon: CogIcon },
    { name: "Contact Management", href: "/admin/contact-management", icon: EnvelopeIcon },
    { name: "Newsletter Management", href: "/admin/newsletter-management", icon: UserGroupIcon },
    { name: "Mobile Control", href: "/admin/mobile-control", icon: DevicePhoneMobileIcon },
    { name: "Operations", href: "/admin/operations", icon: ChatBubbleLeftRightIcon },
    { name: "Announcements", href: "/admin/announcements", icon: MegaphoneIcon },
    { name: "Settings", href: "/admin/settings", icon: CogIcon },
  ];

  // Get current navigation items based on user role and mode
  const getCurrentNavItems = () => {
    if (isAdmin) return adminNavItems;
    if (isAgent && isAgentMode) return agentNavItems;
    return sellerNavItems; // Default to seller mode for agents when not in agent mode
  };

  const currentNavItems = getCurrentNavItems();

  // Use currentNavItems instead of the old navItems logic

  // Helper function to check if a nav item is active
  const isActiveNavItem = (href) => {
    if (href === "/" && location.pathname === "/") return true;
    if (href !== "/" && location.pathname.startsWith(href)) return true;
    return false;
  };

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200">
      {/* First Row - Logo and User Controls */}
      <div className="bg-gradient-to-r from-green-600 to-green-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0 flex items-center">
                <img
                  className="h-20 w-auto"
                  src={logo}
                  alt="Jhonks Logo"
                />
              </Link>
            </div>

            {/* User Controls */}
            <div className="flex items-center space-x-4">
              {/* Bin Status */}
              <Link
                to="/bin"
                className="relative text-white hover:text-green-100 transition-colors"
                title="My Bin"
              >
                <ShoppingCartIcon className="h-6 w-6" />
                {binCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                    {binCount > 99 ? '99+' : binCount}
                  </span>
                )}
              </Link>

              {!isPublic && (
                <>
                  {/* Notifications - Hidden on small screens (moved to second row) */}
                  <Link
                    to="/notifications"
                    className="relative text-white hover:text-green-100 transition-colors"
                    title="Notifications"
                  >
                    <BellIcon className="h-6 w-6" />
                    {notificationCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                        {notificationCount > 99 ? '99+' : notificationCount}
                      </span>
                    )}
                  </Link>
                </>
              )}

              {/* Agent/Seller Toggle - Hidden on small screens (moved to second row) */}
              {isAgent && !isAdmin && (
                <div className="hidden md:flex items-center">
                  <button
                    onClick={handleModeToggle}
                    className="flex items-center space-x-2 px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all duration-200 border border-white border-opacity-30 hover:border-opacity-50"
                    title={`Switch to ${isAgentMode ? 'Seller' : 'Agent'} mode`}
                  >
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${isAgentMode ? 'bg-blue-400' : 'bg-green-400'} shadow-sm`}></div>
                      <span className="text-sm font-semibold text-white">
                        {isAgentMode ? 'Agent Mode' : 'Seller Mode'}
                      </span>
                    </div>
                    <div className="text-xs text-white opacity-80 font-medium">
                      {isAgentMode ? 'Switch to Seller' : 'Switch to Agent'}
                    </div>
                  </button>
                </div>
              )}

              {!isPublic && (
                <>
                  {/* User Profile Dropdown */}
                  <div className="relative" ref={profileDropdownRef}>
                    <button
                      onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                      className="flex items-center space-x-2 text-white hover:text-green-100 transition-colors"
                    >
                      <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        {user?.profileImage ? (
                          <img
                            src={user.profileImage}
                            alt="Profile"
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <UserIcon className="h-5 w-5" />
                        )}
                      </div>
                      <span className="text-sm font-medium hidden sm:block">
                        {user?.firstName || user?.username}
                      </span>
                      <ChevronDownIcon className="h-4 w-4" />
                    </button>

                    {/* Profile Dropdown */}
                    {isProfileDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">
                            {user?.firstName} {user?.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{user?.email}</p>
                        </div>
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setIsProfileDropdownOpen(false)}
                        >
                          <UserIcon className="h-4 w-4 mr-3" />
                          Profile
                        </Link>
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsProfileDropdownOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <svg className="h-4 w-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {isPublic && (
                <div className="flex items-center space-x-2">
                  <Link
                    to="/login"
                    className="px-3 py-2 rounded-lg bg-white bg-opacity-20 text-white hover:bg-opacity-30 transition-colors text-sm font-semibold"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="px-3 py-2 rounded-lg bg-white bg-opacity-10 border border-white border-opacity-30 text-white hover:bg-opacity-20 transition-colors text-sm font-semibold"
                  >
                    Register
                  </Link>
                </div>
              )}

              {/* Mobile menu button */}
              {!isPublic && (
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden text-white hover:text-green-100 p-2 rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors"
                >
                  {isMobileMenuOpen ? (
                    <XMarkIcon className="h-6 w-6" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" />
                  )}
                </button>
              )}

              <WhatsAppButton variant="nav" />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {!isPublic && isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-gray-200 shadow-lg">
          <div className="px-4 pt-2 pb-4 space-y-1">
            {/* Mobile Agent/Seller Toggle */}
            {isAgent && !isAdmin && (
              <div className="px-3 py-3 border-b border-gray-200 mb-2">
                <button
                  onClick={handleModeToggle}
                  className="flex items-center space-x-3 w-full text-left"
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${isAgentMode ? 'bg-blue-400' : 'bg-green-400'} shadow-sm`}></div>
                    <span className="text-base font-semibold text-gray-700">
                      {isAgentMode ? 'Agent Mode' : 'Seller Mode'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 ml-auto">
                    {isAgentMode ? 'Switch to Seller' : 'Switch to Agent'}
                  </div>
                </button>
              </div>
            )}
            
            {currentNavItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 block px-3 py-3 text-base font-medium rounded-lg transition-colors ${
                  isActiveNavItem(item.href)
                    ? 'text-green-600 bg-green-50 border-l-4 border-green-600'
                    : 'text-gray-700 hover:text-green-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="leading-none">{item.name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
